import React, { useEffect, useState } from 'react';

import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Stack,
  Typography,
} from '@mui/material';

// Shared destructive-confirm dialog for removing a saved Stripe
// PaymentMethod. The checkbox is the second confirmation step so a stray
// click on the "Remove" button in the table can't actually detach a card.
// Server errors render inline rather than via toast so the user keeps the
// option to retry without re-opening the dialog.

export default function RemovePaymentMethodDialog({
  open,
  method,
  onClose,
  onConfirm,
}) {
  const [confirmed, setConfirmed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Reset the checkbox + error every time the dialog opens for a fresh
  // method, so prior state from a different PM doesn't carry over.
  useEffect(() => {
    if (open) {
      setConfirmed(false);
      setError(null);
      setSubmitting(false);
    }
  }, [open, method?.id]);

  if (!method) return null;

  const isBank = method.type === 'us_bank_account';
  const isUnverifiedBank = isBank && method.is_verified === false;
  const label = method.label || (isBank ? 'this bank account' : 'this card');

  const handleConfirm = async () => {
    setError(null);
    setSubmitting(true);
    try {
      await onConfirm(method);
    } catch (err) {
      setError(
        err?.response?.data?.detail ||
          err?.message ||
          'Could not remove payment method.',
      );
      setSubmitting(false);
      return;
    }
    setSubmitting(false);
  };

  return (
    <Dialog
      open={open}
      onClose={submitting ? undefined : onClose}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle>Remove payment method?</DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <Box>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="subtitle1">{label}</Typography>
              <Chip
                size="small"
                label={isBank ? 'Bank' : 'Card'}
                color={isBank ? 'success' : 'primary'}
                variant="outlined"
              />
              {isUnverifiedBank && (
                <Chip size="small" label="Pending verification" color="warning" />
              )}
            </Stack>
          </Box>

          <DialogContentText>
            This detaches the payment method from Stripe. It won't be
            available for future auto-charges and won't appear in this
            list anymore. You can re-add it any time.
          </DialogContentText>

          {isUnverifiedBank && (
            <Alert severity="info">
              This bank hasn't finished microdeposit verification yet.
              Removing it cancels that verification — you'd need to start
              over to use it for autopay.
            </Alert>
          )}

          {error && <Alert severity="error">{error}</Alert>}

          <FormControlLabel
            control={
              <Checkbox
                checked={confirmed}
                onChange={(e) => setConfirmed(e.target.checked)}
                disabled={submitting}
              />
            }
            label={`Yes, remove ${label}.`}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting} autoFocus>
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          color="error"
          variant="contained"
          disabled={!confirmed || submitting}
          startIcon={
            submitting ? <CircularProgress size={14} color="inherit" /> : null
          }
        >
          Remove
        </Button>
      </DialogActions>
    </Dialog>
  );
}
